import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import * as Sentry from '@sentry/nextjs';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { z } from 'zod';
import {
  CONTACT_COACH_API,
  PostRequestPayload,
  PostResponsePayload,
} from 'constants/payloads/contactCoach';
import { useApiGateway } from 'hooks/useApi';
import { useGetCurrentUser } from 'hooks/useGetCurrentUser';
import { useViewer } from 'hooks/useViewer';
import { Button } from 'components/Button';
import classNames from 'styles/utils/classNames';
import { Coach } from '../types';

// Form validation schema
const CONTACT_INFO_CONFIRMED_KEY = 'bouce-contact_coach_info_confirmed';

const contactCoachSchema = z.object({
  message: z.string().min(1, 'Please enter a message'),
  confirmContactInfo: z.literal(true, {
    errorMap: () => ({ message: 'You must confirm to proceed' }),
  }),
});

type ContactCoachFormType = z.infer<typeof contactCoachSchema>;

interface ContactCoachProps {
  coachData?: Coach;
  starterId?: string;
  className?: string;
  openAuthModal: () => void;
  visible: boolean;
  readOnly?: boolean;
  onSuccess?: () => void;
}

export default function ContactCoach({
  coachData,
  starterId,
  className,
  openAuthModal,
  visible,
  readOnly = false,
  onSuccess,
}: ContactCoachProps) {
  // Extract relevant coach data
  const coachId = coachData?.id || '';
  const coachName = coachData?.fullName || coachData?.preferredName || '';

  const { post: submitContactForm, isLoading: isSubmitting } = useApiGateway<
    PostRequestPayload,
    PostResponsePayload
  >(CONTACT_COACH_API);

  const viewer = useViewer();
  const user = useGetCurrentUser();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<ContactCoachFormType>({
    resolver: zodResolver(contactCoachSchema),
  });

  useEffect(() => {
    if (visible) {
      const hasConfirmed = localStorage.getItem(CONTACT_INFO_CONFIRMED_KEY);
      if (hasConfirmed === 'true') {
        setValue('confirmContactInfo', true);
      }
    }
  }, [visible, setValue]);

  if (!visible) return null;

  const onSubmit = async (data: ContactCoachFormType) => {
    if (!coachId || isSubmitting) return;

    // Check if user is authenticated
    if (!viewer?.userId) {
      if (openAuthModal) {
        openAuthModal();
      }
      return;
    }

    try {
      // Calculate day boundaries in user's local timezone
      const now = new Date();
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

      const payload: PostRequestPayload = {
        coachId,
        coachStarterProfileId: starterId,
        message: data.message,
        coachName,
        startOfDay: startOfDay.toISOString(),
        endOfDay: endOfDay.toISOString(),
      };

      const res = await submitContactForm({ payload });

      if (res?.isError) {
        toast.error(res?.error as string);
        return;
      }

      localStorage.setItem(CONTACT_INFO_CONFIRMED_KEY, 'true');

      reset({
        message: '',
        confirmContactInfo: true,
      });
      onSuccess?.();
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          feature: 'contact-coach',
          type: 'submission-error',
        },
        extra: {
          coachId,
          coachName,
          message: data.message,
        },
      });
    }
  };

  return (
    <div
      className={classNames(
        'rounded-3xl bg-color-bg-lightmode-primary p-6 shadow-[0px_4px_16px_0px_rgba(0,0,0,0.08)] dark:border-color-border-input-darkmode dark:bg-color-bg-darkmode-primary',
        className,
      )}
    >
      {/* Title */}
      <h2 className="typography-product-heading-compact mb-6 text-color-text-brand">
        Contact Coach
      </h2>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Message Input Section */}
        <div className="space-y-3">
          <label
            htmlFor="message"
            className="typography-product-subheading block text-color-text-lightmode-primary dark:text-color-text-darkmode-primary"
          >
            Type your message
          </label>

          <div className="relative">
            <textarea
              id="message"
              placeholder="Text"
              rows={4}
              {...register('message')}
              className={classNames(
                'w-full resize-none rounded-lg border-0 px-3.5 py-2.5',
                'bg-color-bg-input-lightmode-primary dark:bg-color-bg-input-darkmode-primary',
                'text-color-text-lightmode-primary dark:text-color-text-darkmode-primary',
                'placeholder:text-color-text-lightmode-placeholder dark:placeholder:text-color-text-darkmode-placeholder',
                'typography-product-body',
                'focus:outline-none focus:ring-2 focus:ring-color-text-brand focus:ring-offset-2',
                'focus:ring-offset-color-bg-lightmode-primary dark:focus:ring-offset-color-bg-darkmode-primary',
                errors.message && 'ring-2 ring-color-text-lightmode-error',
              )}
            />
          </div>

          {errors.message && (
            <p className="text-xs text-color-text-lightmode-error dark:text-color-text-darkmode-error">
              {errors.message.message}
            </p>
          )}
        </div>

        {/* Confirmation Checkbox */}
        <div className="flex items-start space-x-3">
          <div className="flex h-6 items-center">
            <input
              id="confirmContactInfo"
              type="checkbox"
              {...register('confirmContactInfo')}
              className="h-4 w-4 rounded border-gray-300 text-color-text-brand focus:ring-color-text-brand dark:border-gray-600 dark:bg-gray-700"
            />
          </div>
          <div className="text-sm">
            <label
              htmlFor="confirmContactInfo"
              className="typography-product-body text-pretty text-sm text-color-text-lightmode-primary dark:text-color-text-darkmode-primary"
            >
              I consent to sharing my contact information with the coach
            </label>
            {errors.confirmContactInfo && (
              <p className="mt-1 text-xs text-color-text-lightmode-error dark:text-color-text-darkmode-error">
                {errors.confirmContactInfo.message}
              </p>
            )}
          </div>
        </div>

        {/* Send Button */}
        <Button
          type="submit"
          variant="brand"
          size="lg"
          className="h-14 w-full rounded-full"
          disabled={!coachId || readOnly}
          loading={isSubmitting}
        >
          {isSubmitting ? 'Sending...' : 'Send'}
        </Button>
      </form>
    </div>
  );
}
